import React, { useState, useEffect } from 'react';

// Enhanced Student Management for NPBC
const StudentManagement = () => {
  const [students, setStudents] = useState([]);
  const [courses, setCourses] = useState([]);
  const [campuses, setCampuses] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterCampus, setFilterCampus] = useState('all');
  const [filterCourse, setFilterCourse] = useState('all');
  const [sortBy, setSortBy] = useState('name');
  const [sortOrder, setSortOrder] = useState('asc');
  const [selectedStudents, setSelectedStudents] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddStudent, setShowAddStudent] = useState(false);
  const [showPreRegisterStudent, setShowPreRegisterStudent] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [studentsPerPage] = useState(10);
  const [activeTab, setActiveTab] = useState('students'); // 'students' or 'pre-register'

  // Modals for actions
  const [viewStudent, setViewStudent] = useState(null);
  const [editStudent, setEditStudent] = useState(null);
  const [deleteStudent, setDeleteStudent] = useState(null);
  const [financialStudent, setFinancialStudent] = useState(null); // new
  const [financialData, setFinancialData] = useState({}); // cache: studentId → data
  const [financialLoading, setFinancialLoading] = useState(false);

  // Edit form data
  const [editFormData, setEditFormData] = useState({});

  // Pre-registration form data
  const [preRegisterFormData, setPreRegisterFormData] = useState({
    name: '',
    email: '',
    phoneNumber: '',
    courseId: '',
    campusId: '',
    admissionNumber: ''
  });

  // Notification state
  const [notification, setNotification] = useState(null);

  // Bulk registration state
  const [registrationMode, setRegistrationMode] = useState('single'); // 'single' or 'bulk'
  const [bulkFile, setBulkFile] = useState(null);
  const [isProcessingBulk, setIsProcessingBulk] = useState(false);
  const [bulkResults, setBulkResults] = useState(null);

  // Set edit form data when editStudent changes
  useEffect(() => {
    if (editStudent) {
      setEditFormData({
        name: editStudent.name,
        email: editStudent.email,
        phoneNumber: editStudent.phoneNumber,
        courseId: editStudent.courseId,
        campusId: editStudent.campusId,
        status: editStudent.status,
      });
    }
  }, [editStudent]);

  // Notification helpers
  const showSuccess = (message) => {
    setNotification({ type: 'success', message });
    setTimeout(() => setNotification(null), 5000);
  };

  const showError = (message) => {
    setNotification({ type: 'error', message });
    setTimeout(() => setNotification(null), 5000);
  };

  // API base URL - adjust this to match your backend
  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.intuitlms.online/api';

  // Fetch students from backend
  const fetchStudents = async () => {
    try {
      setIsLoading(true);
      setError(null);
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/students`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch students: ${response.status} ${response.statusText}`);
      }

      const studentsData = await response.json();

      // Enhance student data with additional course information
      const enhancedStudents = await Promise.all(
        studentsData.map(async (student) => {
          try {
            // Fetch course details for each student
            const token = localStorage.getItem('token');
            const courseResponse = await fetch(`${API_BASE_URL}/courses/${student.courseId}`, {
              headers: {
                'Authorization': `Bearer ${token}`
              }
            });
            const courseData = courseResponse.ok ? await courseResponse.json() : null;

            return {
              ...student,
              course: courseData || { id: student.courseId, name: 'Unknown Course', description: '' },
              // Add computed/default values for enhanced UI
              status: 'active', // Default status - you may want to add this to the database
              yearOfStudy: 1, // Default year - consider adding to database
              semester: 'Current Semester',
              enrollmentDate: student.createdAt || new Date().toISOString(),
              // Financial data - consider adding these to your database
              feeBalance: 0,
              totalFees: 0,
              paidAmount: 0,
              // Academic data - consider adding these to your database
              gpa: 0.0,
              unitsEnrolled: 0,
              attendance: 0
            };
          } catch (courseError) {
            console.error('Error fetching course for student:', student.id, courseError);
            return {
              ...student,
              course: { id: student.courseId, name: 'Unknown Course', description: '' },
              status: 'active',
              yearOfStudy: 1,
              semester: 'Current Semester',
              enrollmentDate: student.createdAt || new Date().toISOString(),
              feeBalance: 0,
              totalFees: 0,
              paidAmount: 0,
              gpa: 0.0,
              unitsEnrolled: 0,
              attendance: 0
            };
          }
        })
      );

      setStudents(enhancedStudents);

      // After loading students, prefetch financial summaries
      const token2 = localStorage.getItem('token');
      const summaries = {};
      await Promise.all(enhancedStudents.map(async (st) => {
        try {
          const r = await fetch(`${API_BASE_URL}/payments/admin/student-financial/${st.id}`, {
            headers: { 'Authorization': `Bearer ${token2}` }
          });
          if (r.ok) summaries[st.id] = await r.json();
        } catch (_) {}
      }));
      setFinancialData(summaries);
    } catch (error) {
      console.error('Error fetching students:', error);
      setError('Failed to load students. Please check your connection and try again.');
      setStudents([]);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch courses from backend
  const fetchCourses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/courses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const coursesData = await response.json();
        setCourses(coursesData);
      }
    } catch (error) {
      console.error('Error fetching courses:', error);
    }
  };

  // Fetch campuses from backend
  const fetchCampuses = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_BASE_URL}/campuses`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const campusesData = await response.json();
        setCampuses(campusesData);
      }
    } catch (error) {
      console.error('Error fetching campuses:', error);
    }
  };

  useEffect(() => {
    fetchStudents();
    fetchCourses();
    fetchCampuses();
  }, []);

  // Filter and search logic
  const filteredStudents = students.filter(student => {
    const matchesSearch = student.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      student.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCampus = filterCampus === 'all' ||
      (student.campus && student.campus.name === filterCampus);

    const matchesCourse = filterCourse === 'all' ||
      (student.course && student.course.name.includes(filterCourse));

    return matchesSearch && matchesCampus && matchesCourse;
  });

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, filterCampus, filterCourse]);

  // Sort logic
  const sortedStudents = [...filteredStudents].sort((a, b) => {
    let aValue = a[sortBy];
    let bValue = b[sortBy];

    if (sortBy === 'campus') {
      aValue = a.campus.name;
      bValue = b.campus.name;
    } else if (sortBy === 'course') {
      aValue = a.course.name;
      bValue = b.course.name;
    }

    if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
    if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
    return 0;
  });

  // Pagination logic
  const indexOfLastStudent = currentPage * studentsPerPage;
  const indexOfFirstStudent = indexOfLastStudent - studentsPerPage;
  const currentStudents = sortedStudents.slice(indexOfFirstStudent, indexOfLastStudent);
  const totalPages = Math.ceil(sortedStudents.length / studentsPerPage);

  const getStatusColor = (status) => {
    switch (status) {
      case 'active': return '#10B981';
      case 'probation': return '#F59E0B';
      case 'suspended': return '#EF4444';
      case 'graduated': return '#8B5CF6';
      default: return '#6B7280';
    }
  };

  const getStatusBackground = (status) => {
    switch (status) {
      case 'active': return '#D1FAE5';
      case 'probation': return '#FEF3C7';
      case 'suspended': return '#FEE2E2';
      case 'graduated': return '#EDE9FE';
      default: return '#F3F4F6';
    }
  };

  // Icons
  const SearchIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <circle cx="11" cy="11" r="8"></circle>
      <path d="M21 21l-4.35-4.35"></path>
    </svg>
  );

  const FilterIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"></polygon>
    </svg>
  );

  const PlusIcon = () => (
    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <line x1="12" y1="5" x2="12" y2="19"></line>
      <line x1="5" y1="12" x2="19" y2="12"></line>
    </svg>
  );

  const DownloadIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
      <polyline points="7 10 12 15 17 10"></polyline>
      <line x1="12" y1="15" x2="12" y2="3"></line>
    </svg>
  );

  const EditIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"></path>
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"></path>
    </svg>
  );

  const EyeIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"></path>
      <circle cx="12" cy="12" r="3"></circle>
    </svg>
  );

  const TrashIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <polyline points="3 6 5 6 21 6"></polyline>
      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
    </svg>
  );

  const UserIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
      <circle cx="12" cy="7" r="4"></circle>
    </svg>
  );

  const MailIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
      <polyline points="22 6 12 13 2 6"></polyline>
    </svg>
  );

  const PhoneIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
      <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
    </svg>
  );

  const fetchStudentFinancial = async (student) => {
    setFinancialStudent(student);
    if (financialData[student.id]) return; // already cached
    setFinancialLoading(true);
    try {
      const token = localStorage.getItem('token');
      const r = await fetch(`${API_BASE_URL}/payments/admin/student-financial/${student.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (r.ok) {
        const data = await r.json();
        setFinancialData(prev => ({ ...prev, [student.id]: data }));
      }
    } catch (_) {}
    setFinancialLoading(false);
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#F8FAFC',
      padding: '24px',
    }}>
      {/* Notification */}
      {notification && (
        <div style={{
          position: 'fixed',
          top: '20px',
          right: '20px',
          zIndex: 2000,
          padding: '12px 16px',
          borderRadius: '8px',
          fontSize: '14px',
          fontWeight: '500',
          color: 'white',
          backgroundColor: notification.type === 'success' ? '#10B981' : '#EF4444',
          boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
          animation: 'slideIn 0.3s ease-out',
        }}>
          {notification.message}
        </div>
      )}

      {/* Header */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        padding: '24px',
        marginBottom: '24px',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div>
            <h1 style={{
              fontSize: '28px',
              fontWeight: 'bold',
              background: 'linear-gradient(45deg, #8B5CF6 30%, #06B6D4 90%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
              margin: '0 0 8px 0',
            }}>
              Student Management
            </h1>
            <p style={{ margin: 0, color: '#6B7280', fontSize: '16px' }}>
              Manage student records, enrollments, and academic progress
            </p>
          </div>
        </div>

        {/* Tab Navigation */}
        <div style={{ display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #E5E7EB' }}>
          <button
            onClick={() => setActiveTab('students')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'students' ? '#8B5CF6' : 'transparent',
              color: activeTab === 'students' ? 'white' : '#6B7280',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Students ({students.length})
          </button>
          <button
            onClick={() => setActiveTab('pre-register')}
            style={{
              padding: '12px 24px',
              backgroundColor: activeTab === 'pre-register' ? '#8B5CF6' : 'transparent',
              color: activeTab === 'pre-register' ? 'white' : '#6B7280',
              border: 'none',
              borderRadius: '8px 8px 0 0',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer',
              transition: 'all 0.2s',
            }}
          >
            Pre-register Students
          </button>
        </div>

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
          <div></div>

          <div style={{ display: 'flex', gap: '12px' }}>
            {/* Placeholder buttons for future functionality */}
            <button style={{
              padding: '8px 16px',
              backgroundColor: '#F3F4F6',
              border: '1px dashed #D1D5DB',
              borderRadius: '8px',
              fontSize: '14px',
              color: '#6B7280',
              cursor: 'not-allowed',
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}>
              <DownloadIcon />
              Import Students
            </button>

            <button
              onClick={() => setShowPreRegisterStudent(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10B981',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#059669'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#10B981'}
            >
              <PlusIcon />
              Pre-register Student
            </button>

            <button
              onClick={() => setShowAddStudent(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer',
                transition: 'background-color 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
              }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#7C3AED'}
              onMouseLeave={(e) => e.target.style.backgroundColor = '#8B5CF6'}
            >
              <PlusIcon />
              Add Student
            </button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          gap: '16px',
          marginBottom: '24px',
        }}>
          {[
            { label: 'Total Students', value: students.length, color: '#8B5CF6' },
            { label: 'Active Students', value: students.filter(s => s.status === 'active').length, color: '#10B981' },
            { label: 'On Probation', value: students.filter(s => s.status === 'probation').length, color: '#F59E0B' },
            { label: 'Suspended', value: students.filter(s => s.status === 'suspended').length, color: '#EF4444' }
          ].map((stat, index) => (
            <div key={index} style={{
              padding: '16px',
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              border: `2px solid ${stat.color}20`,
              textAlign: 'center',
            }}>
              <div style={{ fontSize: '24px', fontWeight: 'bold', color: stat.color, marginBottom: '4px' }}>
                {stat.value}
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>{stat.label}</div>
            </div>
          ))}
        </div>

        {/* Search and Filters */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr auto auto auto auto',
          gap: '12px',
          alignItems: 'center',
        }}>
          <div style={{ position: 'relative' }}>
            <div style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#9CA3AF',
              pointerEvents: 'none',
            }}>
              <SearchIcon />
            </div>
            <input
              type="text"
              placeholder="Search students by name, email, or ID..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                paddingLeft: '40px',
                paddingRight: '16px',
                paddingTop: '10px',
                paddingBottom: '10px',
                backgroundColor: '#F3F4F6',
                border: '1px solid #E5E7EB',
                borderRadius: '8px',
                fontSize: '14px',
                outline: 'none',
                transition: 'all 0.2s',
                width: '100%',
              }}
              onFocus={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.borderColor = '#8B5CF6';
                e.target.style.boxShadow = '0 0 0 3px rgba(139, 92, 246, 0.1)';
              }}
              onBlur={(e) => {
                e.target.style.backgroundColor = '#F3F4F6';
                e.target.style.borderColor = '#E5E7EB';
                e.target.style.boxShadow = 'none';
              }}
            />
          </div>

          <select
            value={filterCampus}
            onChange={(e) => setFilterCampus(e.target.value)}
            style={{
              padding: '10px 12px',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '140px',
            }}
          >
            <option value="all">All Campuses</option>
            {campuses.map(campus => (
              <option key={campus.id} value={campus.name}>{campus.name}</option>
            ))}
          </select>

          <select
            value={filterCourse}
            onChange={(e) => setFilterCourse(e.target.value)}
            style={{
              padding: '10px 12px',
              backgroundColor: '#F9FAFB',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              fontSize: '14px',
              outline: 'none',
              cursor: 'pointer',
              minWidth: '140px',
            }}
          >
            <option value="all">All Courses</option>
            {courses.map(course => (
              <option key={course.id} value={course.name}>{course.name}</option>
            ))}
          </select>

          <button style={{
            padding: '10px 16px',
            backgroundColor: '#F3F4F6',
            border: '1px solid #E5E7EB',
            borderRadius: '8px',
            fontSize: '14px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            transition: 'background-color 0.2s',
          }}
            onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
            onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
          >
            <FilterIcon />
            Export
          </button>
        </div>
      </div>

      {/* Students Table */}
      <div style={{
        backgroundColor: 'white',
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      }}>
        <div style={{
          padding: '20px',
          borderBottom: '1px solid #F3F4F6',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}>
          <div>
            <h3 style={{ margin: '0 0 4px 0', fontSize: '18px', fontWeight: '600', color: '#111827' }}>
              Student Records ({filteredStudents.length})
            </h3>
            <p style={{ margin: 0, fontSize: '14px', color: '#6B7280' }}>
              Showing {indexOfFirstStudent + 1}-{Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} students
            </p>
          </div>

          {/* Placeholder for bulk actions */}
          <div style={{
            padding: '8px 16px',
            backgroundColor: '#F3F4F6',
            border: '1px dashed #D1D5DB',
            borderRadius: '8px',
            fontSize: '12px',
            color: '#6B7280',
            opacity: 0.6,
          }}>
            Bulk Actions (Coming Soon)
          </div>
        </div>

        {isLoading ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            color: '#6B7280',
          }}>
            <div style={{
              width: '40px',
              height: '40px',
              border: '4px solid #F3F4F6',
              borderTop: '4px solid #8B5CF6',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite',
              margin: '0 auto 16px',
            }} />
            Loading students...
          </div>
        ) : currentStudents.length === 0 ? (
          <div style={{
            padding: '60px',
            textAlign: 'center',
            color: '#6B7280',
          }}>
            <UserIcon />
            <p style={{ margin: '16px 0 0 0', fontSize: '16px' }}>No students found</p>
            <p style={{ margin: '8px 0 0 0', fontSize: '14px' }}>Try adjusting your search or filters</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead style={{ backgroundColor: '#F9FAFB' }}>
                <tr>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Student Details
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Program & Status
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Academic Info
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'left',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Financial Status
                  </th>
                  <th style={{
                    padding: '12px 16px',
                    textAlign: 'center',
                    fontSize: '12px',
                    fontWeight: '600',
                    color: '#6B7280',
                    textTransform: 'uppercase',
                    letterSpacing: '0.05em',
                    borderBottom: '1px solid #E5E7EB',
                  }}>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody>
                {currentStudents.map((student, index) => (
                  <tr
                    key={student.id}
                    style={{
                      borderBottom: '1px solid #F3F4F6',
                      transition: 'background-color 0.2s',
                    }}
                    onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#F9FAFB'}
                    onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                  >
                    {/* Student Details */}
                    <td style={{ padding: '16px', minWidth: '280px' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div style={{
                          width: '48px',
                          height: '48px',
                          borderRadius: '12px',
                          background: `linear-gradient(45deg, ${getStatusColor(student.status)}, ${getStatusColor(student.status)}80)`,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: 'white',
                          fontWeight: 'bold',
                          fontSize: '16px',
                        }}>
                          {student.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
                        </div>
                        <div>
                          <div style={{ fontWeight: '600', fontSize: '14px', color: '#111827', marginBottom: '2px' }}>
                            {student.name}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6B7280', marginBottom: '2px' }}>
                            <MailIcon />
                            {student.email}
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '6px', fontSize: '12px', color: '#6B7280' }}>
                            <PhoneIcon />
                            {student.phoneNumber}
                          </div>
                        </div>
                      </div>
                    </td>

                    {/* Program & Status */}
                    <td style={{ padding: '16px', minWidth: '200px' }}>
                      <div style={{ marginBottom: '8px' }}>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: '#111827', marginBottom: '2px' }}>
                          {student.course.name}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280', marginBottom: '4px' }}>
                          Year {student.yearOfStudy} • {student.semester}
                        </div>
                        <div style={{ fontSize: '12px', color: '#6B7280' }}>
                          {student.campus.name}
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: '500',
                        textTransform: 'capitalize',
                        backgroundColor: getStatusBackground(student.status),
                        color: getStatusColor(student.status),
                      }}>
                        {student.status}
                      </span>
                    </td>

                    {/* Academic Info */}
                    <td style={{ padding: '16px', minWidth: '150px' }}>
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>GPA: </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '600',
                          color: student.gpa >= 3.5 ? '#10B981' : student.gpa >= 2.5 ? '#F59E0B' : '#EF4444'
                        }}>
                          {student.gpa}/4.0
                        </span>
                      </div>
                      <div style={{ marginBottom: '4px' }}>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Units: </span>
                        <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                          {student.unitsEnrolled}
                        </span>
                      </div>
                      <div>
                        <span style={{ fontSize: '12px', color: '#6B7280' }}>Attendance: </span>
                        <span style={{
                          fontSize: '14px',
                          fontWeight: '500',
                          color: student.attendance >= 90 ? '#10B981' : student.attendance >= 75 ? '#F59E0B' : '#EF4444'
                        }}>
                          {student.attendance}%
                        </span>
                      </div>
                    </td>

                    {/* Financial Status — live mini-summary */}
                    <td style={{ padding: '16px', minWidth: '180px' }}>
                      {(() => {
                        const fin = financialData[student.id];
                        const total = fin?.grandTotal || 0;
                        const paid = fin?.grandPaid || 0;
                        const bal = fin?.grandBalance || 0;
                        const pct = total > 0 ? Math.min((paid / total) * 100, 100) : 0;
                        const statusColor = bal <= 0 ? '#10B981' : pct >= 50 ? '#F59E0B' : '#EF4444';
                        const statusBg = bal <= 0 ? '#D1FAE5' : pct >= 50 ? '#FEF3C7' : '#FEE2E2';
                        const statusLabel = bal <= 0 ? 'Cleared' : pct >= 50 ? 'Partial' : 'Outstanding';
                        return (
                          <div>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '4px' }}>
                              <span style={{ fontSize: '11px', color: '#6B7280' }}>Total: KES {total.toLocaleString()}</span>
                              <span style={{ fontSize: '10px', fontWeight: '700', padding: '2px 6px', borderRadius: '9999px', backgroundColor: statusBg, color: statusColor }}>{statusLabel}</span>
                            </div>
                            <div style={{ width: '100%', height: '5px', backgroundColor: '#E5E7EB', borderRadius: '9999px', overflow: 'hidden', marginBottom: '4px' }}>
                              <div style={{ height: '100%', width: `${pct}%`, backgroundColor: statusColor, borderRadius: '9999px', transition: 'width 0.5s' }} />
                            </div>
                            <div style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ fontSize: '11px', color: '#10B981', fontWeight: '600' }}>Paid: KES {paid.toLocaleString()}</span>
                              <span style={{ fontSize: '11px', color: bal > 0 ? '#EF4444' : '#10B981', fontWeight: '600' }}>Bal: KES {bal.toLocaleString()}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </td>

                    {/* Actions */}
                    <td style={{ padding: '16px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        {/* View Student */}
                        <button
                          style={{
                            padding: '6px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#6B7280',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                          title="View Details"
                          onClick={() => setViewStudent(student)}
                        >
                          <EyeIcon />
                        </button>

                        {/* Edit Student */}
                        <button
                          style={{
                            padding: '6px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#6B7280',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#E5E7EB'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                          title="Edit Student"
                          onClick={() => setEditStudent(student)}
                        >
                          <EditIcon />
                        </button>

                        {/* Financial Details button */}
                        <button
                          style={{
                            padding: '6px 10px',
                            backgroundColor: '#EDE9FE',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#7C3AED',
                            fontSize: '11px',
                            fontWeight: '700',
                            whiteSpace: 'nowrap',
                          }}
                          onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#DDD6FE'}
                          onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#EDE9FE'}
                          title="View Financial Details"
                          onClick={() => fetchStudentFinancial(student)}
                        >
                          💳 Financials
                        </button>

                        {/* Delete Student */}
                        <button
                          style={{
                            padding: '6px',
                            backgroundColor: '#F3F4F6',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s',
                            color: '#EF4444',
                          }}
                          onMouseEnter={(e) => e.target.style.backgroundColor = '#FEE2E2'}
                          onMouseLeave={(e) => e.target.style.backgroundColor = '#F3F4F6'}
                          title="Delete Student"
                          onClick={() => setDeleteStudent(student)}
                        >
                          <TrashIcon />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {totalPages > 1 && (
          <div style={{
            padding: '20px',
            borderTop: '1px solid #F3F4F6',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <div style={{ fontSize: '14px', color: '#6B7280' }}>
              Showing {indexOfFirstStudent + 1}-{Math.min(indexOfLastStudent, filteredStudents.length)} of {filteredStudents.length} students
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              {/* First Page Button */}
              <button
                onClick={() => setCurrentPage(1)}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 1 ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#9CA3AF' : '#374151',
                  display: totalPages > 5 ? 'block' : 'none',
                }}
                title="First Page"
              >
                «
              </button>

              {/* Previous Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                disabled={currentPage === 1}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === 1 ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === 1 ? 'not-allowed' : 'pointer',
                  color: currentPage === 1 ? '#9CA3AF' : '#374151',
                }}
                title="Previous Page"
              >
                ‹
              </button>

              {/* Page Numbers */}
              <div style={{ display: 'flex', gap: '4px' }}>
                {(() => {
                  const pages = [];
                  const maxVisiblePages = 5;
                  let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2));
                  let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1);

                  // Adjust start page if we're near the end
                  if (endPage - startPage + 1 < maxVisiblePages) {
                    startPage = Math.max(1, endPage - maxVisiblePages + 1);
                  }

                  for (let i = startPage; i <= endPage; i++) {
                    pages.push(
                      <button
                        key={i}
                        onClick={() => setCurrentPage(i)}
                        style={{
                          padding: '8px 12px',
                          backgroundColor: currentPage === i ? '#8B5CF6' : 'white',
                          border: '1px solid #E5E7EB',
                          borderRadius: '6px',
                          fontSize: '14px',
                          cursor: 'pointer',
                          color: currentPage === i ? 'white' : '#374151',
                          fontWeight: currentPage === i ? '600' : '400',
                          minWidth: '40px',
                        }}
                      >
                        {i}
                      </button>
                    );
                  }
                  return pages;
                })()}
              </div>

              {/* Next Button */}
              <button
                onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === totalPages ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#9CA3AF' : '#374151',
                }}
                title="Next Page"
              >
                ›
              </button>

              {/* Last Page Button */}
              <button
                onClick={() => setCurrentPage(totalPages)}
                disabled={currentPage === totalPages}
                style={{
                  padding: '8px 12px',
                  backgroundColor: currentPage === totalPages ? '#F3F4F6' : 'white',
                  border: '1px solid #E5E7EB',
                  borderRadius: '6px',
                  fontSize: '14px',
                  cursor: currentPage === totalPages ? 'not-allowed' : 'pointer',
                  color: currentPage === totalPages ? '#9CA3AF' : '#374151',
                  display: totalPages > 5 ? 'block' : 'none',
                }}
                title="Last Page"
              >
                »
              </button>
            </div>
          </div>
        )}
      </div>

      {/* View Student Modal */}
      {viewStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setViewStudent(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                Student Details
              </h2>
              <button
                onClick={() => setViewStudent(null)}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                ✕
              </button>
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
              <div style={{
                width: '80px',
                height: '80px',
                borderRadius: '16px',
                background: `linear-gradient(45deg, ${getStatusColor(viewStudent.status)}, ${getStatusColor(viewStudent.status)}80)`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontWeight: 'bold',
                fontSize: '24px',
              }}>
                {viewStudent.name.split(' ').map(n => n[0]).join('').substring(0, 2)}
              </div>
              <div>
                <h3 style={{ margin: '0 0 4px 0', fontSize: '20px', fontWeight: '600', color: '#111827' }}>
                  {viewStudent.name}
                </h3>
                <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                  Student ID: {viewStudent.id}
                </p>
                <span style={{
                  padding: '4px 8px',
                  borderRadius: '12px',
                  fontSize: '12px',
                  fontWeight: '500',
                  textTransform: 'capitalize',
                  backgroundColor: getStatusBackground(viewStudent.status),
                  color: getStatusColor(viewStudent.status),
                }}>
                  {viewStudent.status}
                </span>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Personal Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <MailIcon />
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>{viewStudent.email}</span>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <PhoneIcon />
                    <span style={{ fontSize: '14px', color: '#6B7280' }}>{viewStudent.phoneNumber}</span>
                  </div>
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Academic Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Course: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.course.name}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Campus: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.campus.name}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Year: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.yearOfStudy}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Semester: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.semester}</span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '24px', marginBottom: '24px' }}>
              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Academic Performance
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>GPA: </span>
                    <span style={{
                      fontSize: '16px',
                      fontWeight: '600',
                      color: viewStudent.gpa >= 3.5 ? '#10B981' : viewStudent.gpa >= 2.5 ? '#F59E0B' : '#EF4444'
                    }}>
                      {viewStudent.gpa}/4.0
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Units Enrolled: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>{viewStudent.unitsEnrolled}</span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Attendance: </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '500',
                      color: viewStudent.attendance >= 90 ? '#10B981' : viewStudent.attendance >= 75 ? '#F59E0B' : '#EF4444'
                    }}>
                      {viewStudent.attendance}%
                    </span>
                  </div>
                </div>
              </div>

              <div>
                <h4 style={{ margin: '0 0 12px 0', fontSize: '16px', fontWeight: '600', color: '#111827' }}>
                  Financial Information
                </h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Total Fees: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#111827' }}>
                      KSH {viewStudent.totalFees.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Amount Paid: </span>
                    <span style={{ fontSize: '14px', fontWeight: '500', color: '#10B981' }}>
                      KSH {viewStudent.paidAmount.toLocaleString()}
                    </span>
                  </div>
                  <div>
                    <span style={{ fontSize: '12px', color: '#6B7280' }}>Balance: </span>
                    <span style={{
                      fontSize: '14px',
                      fontWeight: '600',
                      color: viewStudent.feeBalance === 0 ? '#10B981' : '#EF4444'
                    }}>
                      KSH {viewStudent.feeBalance.toLocaleString()}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setViewStudent(null)}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
              <button
                onClick={() => {
                  setEditStudent(viewStudent);
                  setViewStudent(null);
                }}
                style={{
                  padding: '10px 20px',
                  backgroundColor: '#8B5CF6',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Edit Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Edit Student Modal */}
      {editStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setEditStudent(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                Edit Student
              </h2>
              <button
                onClick={() => setEditStudent(null)}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                ✕
              </button>
            </div>

            <form onSubmit={async (e) => {
              e.preventDefault();
              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/students/${editStudent.id}`, {
                  method: 'PUT',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(editFormData),
                });

                if (response.ok) {
                  showSuccess('Student updated successfully');
                  fetchStudents(); // Refresh the list
                  setEditStudent(null);
                } else {
                  const errorData = await response.json();
                  showError(errorData.message || 'Failed to update student');
                }
              } catch (error) {
                console.error('Error updating student:', error);
                showError('An error occurred while updating the student');
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Full Name
                  </label>
                  <input
                    type="text"
                    value={editFormData.name}
                    onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Email
                  </label>
                  <input
                    type="email"
                    value={editFormData.email}
                    onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Phone Number
                  </label>
                  <input
                    type="tel"
                    value={editFormData.phoneNumber}
                    onChange={(e) => setEditFormData({ ...editFormData, phoneNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Course
                  </label>
                  <select
                    value={editFormData.courseId}
                    onChange={(e) => setEditFormData({ ...editFormData, courseId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Campus
                  </label>
                  <select
                    value={editFormData.campusId}
                    onChange={(e) => setEditFormData({ ...editFormData, campusId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    {campuses.map(campus => (
                      <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Status
                  </label>
                  <select
                    value={editFormData.status}
                    onChange={(e) => setEditFormData({ ...editFormData, status: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    <option value="active">Active</option>
                    <option value="probation">Probation</option>
                    <option value="suspended">Suspended</option>
                    <option value="graduated">Graduated</option>
                  </select>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setEditStudent(null)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#8B5CF6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Save Changes
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Student Modal */}
      {deleteStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setDeleteStudent(null)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '400px',
            width: '90%',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ textAlign: 'center', marginBottom: '24px' }}>
              <div style={{
                width: '64px',
                height: '64px',
                borderRadius: '50%',
                backgroundColor: '#FEE2E2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                margin: '0 auto 16px',
              }}>
                <TrashIcon />
              </div>
              <h2 style={{ margin: '0 0 8px 0', fontSize: '20px', fontWeight: 'bold', color: '#111827' }}>
                Delete Student
              </h2>
              <p style={{ margin: '0', color: '#6B7280', fontSize: '14px' }}>
                Are you sure you want to delete <strong>{deleteStudent.name}</strong>? This action cannot be undone.
              </p>
            </div>

            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={() => setDeleteStudent(null)}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#F3F4F6',
                  color: '#374151',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                onClick={async () => {
                  try {
                    const token = localStorage.getItem('token');
                    const response = await fetch(`${API_BASE_URL}/students/${deleteStudent.id}`, {
                      method: 'DELETE',
                      headers: {
                        'Authorization': `Bearer ${token}`
                      }
                    });

                    if (response.ok) {
                      showSuccess('Student deleted successfully');
                      fetchStudents(); // Refresh the list
                      setDeleteStudent(null);
                    } else {
                      const errorData = await response.json();
                      showError(errorData.message || 'Failed to delete student');
                    }
                  } catch (error) {
                    console.error('Error deleting student:', error);
                    showError('An error occurred while deleting the student');
                  }
                }}
                style={{
                  flex: 1,
                  padding: '12px',
                  backgroundColor: '#EF4444',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                }}
              >
                Delete Student
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Pre-register Student Modal */}
      {showPreRegisterStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setShowPreRegisterStudent(false)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '600px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
              <h2 style={{ margin: '0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
                Pre-register Student
              </h2>
              <button
                onClick={() => setShowPreRegisterStudent(false)}
                style={{
                  padding: '8px',
                  backgroundColor: '#F3F4F6',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  color: '#6B7280',
                }}
              >
                ✕
              </button>
            </div>

            <p style={{ margin: '0 0 24px 0', color: '#6B7280', fontSize: '14px' }}>
              Pre-register a student with their details and generate an admission number. The student can then use this admission number to complete their registration.
            </p>

            {/* Mode Switch */}
            <div style={{
              display: 'flex',
              backgroundColor: '#F3F4F6',
              padding: '4px',
              borderRadius: '10px',
              marginBottom: '24px'
            }}>
              <button
                onClick={() => { setRegistrationMode('single'); setBulkResults(null); }}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: registrationMode === 'single' ? 'white' : 'transparent',
                  color: registrationMode === 'single' ? '#8B5CF6' : '#6B7280',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: registrationMode === 'single' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                Single Student
              </button>
              <button
                onClick={() => { setRegistrationMode('bulk'); setBulkResults(null); }}
                style={{
                  flex: 1,
                  padding: '8px',
                  backgroundColor: registrationMode === 'bulk' ? 'white' : 'transparent',
                  color: registrationMode === 'bulk' ? '#8B5CF6' : '#6B7280',
                  border: 'none',
                  borderRadius: '8px',
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  boxShadow: registrationMode === 'bulk' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none'
                }}
              >
                Bulk Upload (CSV)
              </button>
            </div>

            {registrationMode === 'single' ? (

            <form onSubmit={async (e) => {
              e.preventDefault();

              // Use manual admission number
              const admissionNumber = preRegisterFormData.admissionNumber;

              if (!admissionNumber) {
                showError('Please enter an admission number');
                return;
              }

              const formData = {
                ...preRegisterFormData,
                admissionNumber
              };

              try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/students/pre-register`, {
                  method: 'POST',
                  headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                  },
                  body: JSON.stringify(formData),
                });

                if (response.ok) {
                  const result = await response.json();
                  showSuccess(`Student pre-registered successfully! Admission Number: ${admissionNumber}`);
                  setPreRegisterFormData({
                    name: '',
                    email: '',
                    phoneNumber: '',
                    courseId: '',
                    campusId: '',
                    admissionNumber: ''
                  });
                  setShowPreRegisterStudent(false);
                  fetchStudents(); // Refresh the list
                } else {
                  const errorData = await response.json();
                  showError(errorData.message || 'Failed to pre-register student');
                }
              } catch (error) {
                console.error('Error pre-registering student:', error);
                showError('An error occurred while pre-registering the student');
              }
            }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px', marginBottom: '24px' }}>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Full Name *
                  </label>
                  <input
                    type="text"
                    value={preRegisterFormData.name}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, name: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter student's full name"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Email Address *
                  </label>
                  <input
                    type="email"
                    value={preRegisterFormData.email}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, email: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter student's email"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Phone Number *
                  </label>
                  <input
                    type="tel"
                    value={preRegisterFormData.phoneNumber}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, phoneNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter phone number"
                    required
                  />
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Course *
                  </label>
                  <select
                    value={preRegisterFormData.courseId}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, courseId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    <option value="">Select a course</option>
                    {courses.map(course => (
                      <option key={course.id} value={course.id}>{course.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Campus *
                  </label>
                  <select
                    value={preRegisterFormData.campusId}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, campusId: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    required
                  >
                    <option value="">Select a campus</option>
                    {campuses.map(campus => (
                      <option key={campus.id} value={campus.id}>{campus.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                    Admission Number
                  </label>
                  <input
                    type="text"
                    value={preRegisterFormData.admissionNumber}
                    onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, admissionNumber: e.target.value })}
                    style={{
                      width: '100%',
                      padding: '8px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      outline: 'none',
                    }}
                    placeholder="Enter admission number"
                    required
                  />
                </div>
              </div>

              <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
                <button
                  type="button"
                  onClick={() => setShowPreRegisterStudent(false)}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#F3F4F6',
                    color: '#374151',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#8B5CF6',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    fontSize: '14px',
                    fontWeight: '500',
                    cursor: 'pointer',
                  }}
                >
                  Pre-register Student
                </button>
              </div>
            </form>
            ) : (
              /* Bulk Registration Content */
              <div>
                {bulkResults ? (
                  <div style={{ marginBottom: '24px' }}>
                    <div style={{
                      padding: '16px',
                      backgroundColor: bulkResults.errorCount === 0 ? '#D1FAE5' : '#FEF3C7',
                      borderRadius: '12px',
                      border: `1px solid ${bulkResults.errorCount === 0 ? '#10B981' : '#F59E0B'}`,
                      marginBottom: '16px'
                    }}>
                      <div style={{ fontWeight: 'bold', color: bulkResults.errorCount === 0 ? '#065F46' : '#92400E', fontSize: '16px' }}>
                        {bulkResults.message}
                      </div>
                      <div style={{ fontSize: '14px', marginTop: '4px', color: bulkResults.errorCount === 0 ? '#047857' : '#B45309' }}>
                        Processed Students: {bulkResults.createdCount} | Errors: {bulkResults.errorCount}
                      </div>
                    </div>

                    {bulkResults.errors && bulkResults.errors.length > 0 && (
                      <div style={{
                        maxHeight: '200px',
                        overflowY: 'auto',
                        padding: '12px',
                        backgroundColor: '#FFF5F5',
                        borderRadius: '8px',
                        border: '1px solid #FECACA'
                      }}>
                        <h4 style={{ margin: '0 0 8px 0', fontSize: '14px', color: '#C53030', fontWeight: 'bold' }}>Error Details:</h4>
                        {bulkResults.errors.map((err, i) => (
                          <div key={i} style={{ fontSize: '12px', color: '#742A2A', marginBottom: '4px', borderBottom: '1px solid #FED7D7', paddingBottom: '4px' }}>
                            Row {err.index} ({err.name}): {err.error}
                          </div>
                        ))}
                      </div>
                    )}

                    <button
                      onClick={() => { setBulkResults(null); setBulkFile(null); }}
                      style={{
                        marginTop: '20px',
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#8B5CF6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: 'bold',
                        cursor: 'pointer'
                      }}
                    >
                      Process Another File
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '20px' }}>
                    <div style={{ border: '2px dashed #E5E7EB', borderRadius: '12px', padding: '32px', textAlign: 'center' }}>
                      <div style={{ fontSize: '40px', marginBottom: '12px' }}>📁</div>
                      <h3 style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '8px' }}>Upload Students CSV</h3>
                      <p style={{ fontSize: '13px', color: '#6B7280', marginBottom: '10px' }}>
                        Ensure your file is a <strong>.csv</strong> (save Excel as CSV).
                      </p>
                      <p style={{ fontSize: '12px', color: '#9CA3AF', marginBottom: '20px' }}>
                        Include a header row with columns like: <strong>Full Name, Email, Phone, Admission Number</strong> (order doesn't matter).
                      </p>
                      <input
                        type="file"
                        accept=".csv"
                        onChange={(e) => setBulkFile(e.target.files[0])}
                        style={{ display: 'none' }}
                        id="bulk-csv-upload"
                      />
                      <label 
                        htmlFor="bulk-csv-upload"
                        style={{
                          padding: '10px 24px',
                          backgroundColor: bulkFile ? '#10B981' : '#F3F4F6',
                          color: bulkFile ? 'white' : '#374151',
                          borderRadius: '8px',
                          fontSize: '14px',
                          fontWeight: '600',
                          cursor: 'pointer',
                          display: 'inline-block',
                          transition: 'all 0.2s'
                        }}
                      >
                        {bulkFile ? `Selected: ${bulkFile.name}` : 'Choose CSV File'}
                      </label>
                      {bulkFile && (
                        <div style={{ marginTop: '12px', fontSize: '12px', color: '#10B981', fontWeight: '600' }}>
                           File ready for processing
                        </div>
                      )}
                    </div>

                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Course for all students *
                        </label>
                        <select
                          value={preRegisterFormData.courseId}
                          onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, courseId: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '14px',
                            outline: 'none',
                          }}
                          required
                        >
                          <option value="">Select a course</option>
                          {courses.map(course => (
                            <option key={course.id} value={course.id}>{course.name}</option>
                          ))}
                        </select>
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: '14px', fontWeight: '500', color: '#374151', marginBottom: '4px' }}>
                          Campus for all students *
                        </label>
                        <select
                          value={preRegisterFormData.campusId}
                          onChange={(e) => setPreRegisterFormData({ ...preRegisterFormData, campusId: e.target.value })}
                          style={{
                            width: '100%',
                            padding: '10px 12px',
                            border: '1px solid #D1D5DB',
                            borderRadius: '8px',
                            fontSize: '14px',
                            outline: 'none',
                          }}
                          required
                        >
                          <option value="">Select a campus</option>
                          {campuses.map(campus => (
                            <option key={campus.id} value={campus.id}>{campus.name}</option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <button
                      onClick={async () => {
                        if (!bulkFile) { showError('Please select a CSV file'); return; }
                        if (!preRegisterFormData.courseId || !preRegisterFormData.campusId) {
                          showError('Please select both Course and Campus'); return;
                        }

                        setIsProcessingBulk(true);
                        try {
                          const reader = new FileReader();
                          reader.onload = async (e) => {
                            const text = e.target.result;
                            const lines = text.split(/\r?\n/).filter(line => line.trim());
                            if (lines.length < 2) {
                              showError('CSV file must have a header row and at least one student row.');
                              setIsProcessingBulk(false);
                              return;
                            }

                            // Robust CSV Parser (handles quotes)
                            const parseCSVLine = (line) => {
                              const result = [];
                              let current = '';
                              let inQuotes = false;
                              for (let i = 0; i < line.length; i++) {
                                const char = line[i];
                                if (char === '"') {
                                  inQuotes = !inQuotes;
                                } else if (char === ',' && !inQuotes) {
                                  result.push(current.trim());
                                  current = '';
                                } else {
                                  current += char;
                                }
                              }
                              result.push(current.trim());
                              return result.map(v => v.replace(/^"|"$/g, ''));
                            };

                            const headers = parseCSVLine(lines[0]).map(h => h.toLowerCase());
                            
                            // Map columns by keywords
                            const findIndex = (keywords) => headers.findIndex(h => keywords.some(k => h.includes(k)));
                            
                            const nameIdx = findIndex(['name', 'full name', 'student name']);
                            const emailIdx = findIndex(['email', 'e-mail']);
                            const phoneIdx = findIndex(['phone', 'contact', 'mobile', 'tel']);
                            const admissionIdx = findIndex(['admission', 'admin no', 'reg no']);

                            if (nameIdx === -1 || emailIdx === -1 || phoneIdx === -1 || admissionIdx === -1) {
                              showError('Could not find required columns. Please ensure your CSV has headers for Name, Email, Phone, and Admission Number.');
                              setIsProcessingBulk(false);
                              return;
                            }

                            const students = [];
                            for(let i = 1; i < lines.length; i++) {
                              const parts = parseCSVLine(lines[i]);
                              if (parts.length > Math.max(nameIdx, emailIdx, phoneIdx, admissionIdx)) {
                                students.push({
                                  name: parts[nameIdx],
                                  email: parts[emailIdx],
                                  phoneNumber: parts[phoneIdx],
                                  admissionNumber: parts[admissionIdx],
                                  courseId: preRegisterFormData.courseId,
                                  campusId: preRegisterFormData.campusId
                                });
                              }
                            }

                            if (students.length === 0) {
                              showError('No valid student data found in CSV');
                              setIsProcessingBulk(false);
                              return;
                            }

                            // Call bulk endpoint
                            const token = localStorage.getItem('token');
                            const response = await fetch(`${API_BASE_URL}/students/pre-register/bulk`, {
                              method: 'POST',
                              headers: {
                                'Content-Type': 'application/json',
                                'Authorization': `Bearer ${token}`,
                              },
                              body: JSON.stringify({ students }),
                            });

                            const data = await response.json();
                            if (response.ok) {
                              setBulkResults(data);
                              fetchStudents();
                            } else {
                              showError(data.message || 'Bulk registration failed');
                            }
                            setIsProcessingBulk(false);
                          };
                          reader.readAsText(bulkFile);
                        } catch (err) {
                          console.error(err);
                          showError('Failed to process bulk registration');
                          setIsProcessingBulk(false);
                        }
                      }}
                      disabled={isProcessingBulk}
                      style={{
                        width: '100%',
                        padding: '12px',
                        backgroundColor: '#8B5CF6',
                        color: 'white',
                        border: 'none',
                        borderRadius: '10px',
                        fontWeight: 'bold',
                        fontSize: '16px',
                        cursor: 'pointer',
                        opacity: isProcessingBulk ? 0.7 : 1,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '10px'
                      }}
                    >
                      {isProcessingBulk ? 'Processing Bulk Registration...' : '🚀 Start Bulk Pre-registration'}
                    </button>

                    <button
                      onClick={() => setShowPreRegisterStudent(false)}
                      style={{
                        width: '100%',
                        padding: '10px',
                        backgroundColor: 'transparent',
                        color: '#6B7280',
                        border: 'none',
                        fontSize: '14px',
                        cursor: 'pointer'
                      }}
                    >
                      Close Modal
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Placeholder for Add Student Modal */}
      {showAddStudent && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0,0,0,0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1060,
        }}
          onClick={() => setShowAddStudent(false)}
        >
          <div style={{
            backgroundColor: 'white',
            borderRadius: '16px',
            padding: '32px',
            maxWidth: '500px',
            width: '90%',
            maxHeight: '80%',
            overflow: 'auto',
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1)',
          }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ margin: '0 0 16px 0', fontSize: '24px', fontWeight: 'bold', color: '#111827' }}>
              Add New Student
            </h2>
            <p style={{ margin: '0 0 24px 0', color: '#6B7280' }}>
              This feature is coming soon. The student registration form will include comprehensive fields for academic and personal information.
            </p>
            <div style={{
              padding: '20px',
              backgroundColor: '#F9FAFB',
              borderRadius: '12px',
              border: '2px dashed #E5E7EB',
              textAlign: 'center',
              marginBottom: '24px',
            }}>
              <div style={{ fontSize: '48px', marginBottom: '12px' }}>📝</div>
              <div style={{ fontSize: '16px', fontWeight: '500', color: '#374151', marginBottom: '8px' }}>
                Student Registration Form
              </div>
              <div style={{ fontSize: '14px', color: '#6B7280' }}>
                Will include fields for personal info, academic program, fees, guardian details, and more
              </div>
            </div>
            <button
              onClick={() => setShowAddStudent(false)}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#8B5CF6',
                color: 'white',
                border: 'none',
                borderRadius: '8px',
                fontSize: '16px',
                fontWeight: '500',
                cursor: 'pointer',
              }}
            >
              Close
            </button>
          </div>
        </div>
      )}

      {/* ── Student Financial Details Modal ───────────────────────────────── */}
      {financialStudent && (
        <div style={{
          position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.6)',
          display: 'flex', alignItems: 'flex-start', justifyContent: 'flex-end',
          zIndex: 1100, overflowY: 'auto',
        }} onClick={() => setFinancialStudent(null)}>
          <div style={{
            backgroundColor: 'white', width: '100%', maxWidth: '780px',
            minHeight: '100vh', padding: '0', display: 'flex', flexDirection: 'column',
            boxShadow: '-8px 0 40px rgba(0,0,0,0.2)',
          }} onClick={(e) => e.stopPropagation()}>

            {/* Header */}
            <div style={{
              background: 'linear-gradient(135deg, #1e1b4b 0%, #4c1d95 100%)',
              padding: '24px 28px', color: 'white',
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <p style={{ margin: '0 0 4px', fontSize: '11px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#a5b4fc', fontWeight: '700' }}>
                    Student Financial Report
                  </p>
                  <h2 style={{ margin: '0 0 4px', fontSize: '22px', fontWeight: '900' }}>{financialStudent.name}</h2>
                  <p style={{ margin: 0, fontSize: '13px', color: '#c4b5fd' }}>
                    {financialStudent.email}
                    {financialStudent.admissionNumber && ` • ID: ${financialStudent.admissionNumber}`}
                    {financialStudent.campus?.name && ` • ${financialStudent.campus.name} Campus`}
                  </p>
                </div>
                <button onClick={() => setFinancialStudent(null)} style={{
                  background: 'rgba(255,255,255,0.15)', border: 'none', borderRadius: '8px',
                  color: 'white', cursor: 'pointer', padding: '8px 12px', fontSize: '13px', fontWeight: '700',
                }}>✕ Close</button>
              </div>

              {/* Grand summary */}
              {financialData[financialStudent.id] && (() => {
                const fin = financialData[financialStudent.id];
                const pct = fin.grandTotal > 0 ? Math.min((fin.grandPaid / fin.grandTotal) * 100, 100) : 0;
                return (
                  <div>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '12px', marginTop: '20px' }}>
                      {[
                        { label: 'Total Fees', value: fin.grandTotal, color: '#e0e7ff', bg: 'rgba(255,255,255,0.1)' },
                        { label: 'Total Paid', value: fin.grandPaid, color: '#bbf7d0', bg: 'rgba(16,185,129,0.2)' },
                        { label: 'Balance Due', value: fin.grandBalance, color: fin.grandBalance > 0 ? '#fecaca' : '#bbf7d0', bg: fin.grandBalance > 0 ? 'rgba(239,68,68,0.2)' : 'rgba(16,185,129,0.2)' },
                      ].map(item => (
                        <div key={item.label} style={{ padding: '12px', borderRadius: '10px', backgroundColor: item.bg, textAlign: 'center' }}>
                          <p style={{ margin: '0 0 4px', fontSize: '10px', textTransform: 'uppercase', letterSpacing: '0.08em', color: 'rgba(255,255,255,0.7)', fontWeight: '700' }}>{item.label}</p>
                          <p style={{ margin: 0, fontSize: '18px', fontWeight: '900', color: item.color }}>KES {item.value.toLocaleString()}</p>
                        </div>
                      ))}
                    </div>
                    <div style={{ marginTop: '12px' }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                        <span style={{ fontSize: '11px', color: 'rgba(255,255,255,0.7)' }}>Overall Payment Progress</span>
                        <span style={{ fontSize: '11px', color: 'white', fontWeight: '700' }}>{pct.toFixed(0)}%</span>
                      </div>
                      <div style={{ height: '6px', backgroundColor: 'rgba(255,255,255,0.2)', borderRadius: '9999px', overflow: 'hidden' }}>
                        <div style={{ height: '100%', width: `${pct}%`, backgroundColor: fin.grandBalance <= 0 ? '#34d399' : pct >= 50 ? '#fbbf24' : '#f87171', borderRadius: '9999px', transition: 'width 0.8s' }} />
                      </div>
                    </div>
                  </div>
                );
              })()}
            </div>

            {/* Export Actions */}
            <div style={{ padding: '16px 28px', borderBottom: '1px solid #f3f4f6', display: 'flex', gap: '10px', alignItems: 'center', backgroundColor: '#fafafa' }}>
              <span style={{ fontSize: '13px', color: '#6b7280', fontWeight: '600' }}>Export:</span>
              <button
                onClick={() => {
                  const fin = financialData[financialStudent.id];
                  if (!fin) return;
                  const rows = [['Unit', 'Course', 'Enrollment', 'Payment Status', 'Total (KES)', 'Paid (KES)', 'Balance (KES)', 'Progress %']];
                  fin.unitSummaries.forEach(u => {
                    rows.push([u.unitName, u.courseName, u.enrollmentStatus, u.paymentStatus, u.totalAmount, u.paidAmount, u.balance, u.progressPct + '%']);
                  });
                  rows.push([]);
                  rows.push(['TOTALS', '', '', '', fin.grandTotal, fin.grandPaid, fin.grandBalance, '']);
                  const csv = rows.map(r => r.map(c => `"${c}"`).join(',')).join('\n');
                  const blob = new Blob([csv], { type: 'text/csv' });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement('a');
                  a.href = url; a.download = `${financialStudent.name.replace(/ /g, '_')}_financial_report.csv`;
                  a.click(); URL.revokeObjectURL(url);
                }}
                style={{ padding: '7px 14px', backgroundColor: '#059669', color: 'white', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
              >
                ⬇ CSV
              </button>
              <button
                onClick={() => {
                  const fin = financialData[financialStudent.id];
                  if (!fin) return;
                  const st = fin.student || financialStudent;
                  const unitRows = (fin.unitSummaries || []).map((u, i) => {
                    const txRows = u.payments.map((p, pi) => `<tr style="background:${pi % 2 === 0 ? '#f9fafb' : 'white'}"><td>${pi + 1}</td><td>${new Date(p.createdAt).toLocaleDateString('en-KE')}</td><td style="text-transform:capitalize">${p.paymentMethod}</td><td>${p.referenceNumber || p.mpesaCode || '—'}</td><td style="text-align:right;font-weight:700">KES ${parseFloat(p.amount).toLocaleString()}</td><td><span style="padding:2px 6px;border-radius:9999px;font-size:10px;font-weight:700;background:${p.status === 'confirmed' ? '#dcfce7' : '#fef9c3'};color:${p.status === 'confirmed' ? '#166534' : '#854d0e'}">${p.status}</span></td></tr>`).join('');
                    return `<div style="margin-bottom:24px;padding:16px;border:1px solid #e5e7eb;border-radius:10px"><div style="display:flex;justify-content:space-between;align-items:center;margin-bottom:10px"><div><p style="margin:0;font-size:14px;font-weight:800">${u.unitName}</p><p style="margin:2px 0 0;font-size:12px;color:#6b7280">${u.courseName}</p></div><span style="padding:3px 10px;border-radius:9999px;font-size:11px;font-weight:700;background:${u.balance <= 0 ? '#dcfce7' : '#fee2e2'};color:${u.balance <= 0 ? '#166534' : '#991b1b'}">${u.balance <= 0 ? 'CLEARED' : 'BALANCE: KES ' + u.balance.toLocaleString()}</span></div><div style="display:grid;grid-template-columns:1fr 1fr 1fr;gap:8px;margin-bottom:12px"><div style="text-align:center;padding:10px;background:#f3f4f6;border-radius:8px"><p style="margin:0;font-size:10px;color:#6b7280;font-weight:700;text-transform:uppercase">Total</p><p style="margin:4px 0 0;font-size:16px;font-weight:900">KES ${u.totalAmount.toLocaleString()}</p></div><div style="text-align:center;padding:10px;background:#f0fdf4;border-radius:8px"><p style="margin:0;font-size:10px;color:#16a34a;font-weight:700;text-transform:uppercase">Paid</p><p style="margin:4px 0 0;font-size:16px;font-weight:900;color:#166534">KES ${u.paidAmount.toLocaleString()}</p></div><div style="text-align:center;padding:10px;background:${u.balance <= 0 ? '#f0fdf4' : '#fff7ed'};border-radius:8px"><p style="margin:0;font-size:10px;color:${u.balance <= 0 ? '#16a34a' : '#c2410c'};font-weight:700;text-transform:uppercase">Balance</p><p style="margin:4px 0 0;font-size:16px;font-weight:900;color:${u.balance <= 0 ? '#166534' : '#9a3412'}">${u.balance <= 0 ? '✓ CLEARED' : 'KES ' + u.balance.toLocaleString()}</p></div></div>${u.payments.length > 0 ? `<table style="width:100%;border-collapse:collapse;font-size:12px"><thead><tr style="background:#1e1b4b;color:white"><th style="padding:8px;text-align:left">#</th><th style="padding:8px;text-align:left">Date</th><th style="padding:8px;text-align:left">Method</th><th style="padding:8px;text-align:left">Ref</th><th style="padding:8px;text-align:right">Amount</th><th style="padding:8px">Status</th></tr></thead><tbody>${txRows}</tbody></table>` : '<p style="font-size:12px;color:#9ca3af;text-align:center">No transactions recorded</p>'}</div>`;
                  }).join('');
                  const html = `<!DOCTYPE html><html><head><title>Financial Report — ${st.name}</title><style>body{font-family:system-ui,sans-serif;margin:0;padding:24px;color:#111}@media print{button{display:none}}</style></head><body><div style="max-width:780px;margin:0 auto"><div style="display:flex;justify-content:space-between;border-bottom:2px solid #1e1b4b;padding-bottom:16px;margin-bottom:24px"><div><h1 style="margin:0;color:#1e1b4b;font-size:20px">${st.institution?.name || 'INTUIT LMS'}</h1><p style="margin:2px 0 0;font-size:13px;color:#6b7280">Student Financial Report${st.campus?.name ? ' — ' + st.campus.name + ' Campus' : ''}</p></div><div style="text-align:right"><p style="margin:0;font-size:12px;color:#6b7280">Issued: ${new Date().toLocaleDateString('en-KE', { day: 'numeric', month: 'long', year: 'numeric' })}</p></div></div><div style="display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:24px"><div><p style="margin:0;font-size:11px;color:#6b7280;font-weight:700;text-transform:uppercase">Student</p><p style="margin:4px 0 0;font-size:15px;font-weight:800">${st.name}</p>${st.admissionNumber ? `<p style="margin:2px 0 0;font-size:12px;color:#6b7280">ID: ${st.admissionNumber}</p>` : ''}</div><div style="text-align:right"><p style="margin:0;font-size:11px;color:#6b7280;font-weight:700;text-transform:uppercase">Summary</p><p style="margin:4px 0 0;font-size:14px">Total: <strong>KES ${fin.grandTotal.toLocaleString()}</strong></p><p style="margin:2px 0 0;font-size:14px">Paid: <strong style="color:#166534">KES ${fin.grandPaid.toLocaleString()}</strong></p><p style="margin:2px 0 0;font-size:14px">Balance: <strong style="color:${fin.grandBalance > 0 ? '#991b1b' : '#166534'}">${fin.grandBalance <= 0 ? 'CLEARED' : 'KES ' + fin.grandBalance.toLocaleString()}</strong></p></div></div>${unitRows}<p style="margin-top:24px;text-align:center;font-size:11px;color:#9ca3af;border-top:1px solid #e5e7eb;padding-top:12px">Computer-generated report. For queries contact your institution.</p></div><script>window.print();</script></body></html>`;
                  const w = window.open('', '_blank'); w.document.write(html); w.document.close();
                }}
                style={{ padding: '7px 14px', backgroundColor: '#7c3aed', color: 'white', border: 'none', borderRadius: '7px', fontSize: '12px', fontWeight: '700', cursor: 'pointer' }}
              >
                🖨 Print Report
              </button>
            </div>

            {/* Per-unit breakdown */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '20px 28px' }}>
              {financialLoading ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                  <div style={{ width: '36px', height: '36px', border: '3px solid #e5e7eb', borderTop: '3px solid #7c3aed', borderRadius: '50%', animation: 'spin 1s linear infinite', margin: '0 auto 12px' }} />
                  Loading financial data...
                </div>
              ) : !financialData[financialStudent.id] ? (
                <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                  <p>No financial data available.</p>
                </div>
              ) : (
                <div>
                  {financialData[financialStudent.id].unitSummaries.map((unit, ui) => (
                    <div key={unit.unitId} style={{ marginBottom: '20px', border: '1px solid #e5e7eb', borderRadius: '12px', overflow: 'hidden' }}>
                      {/* Unit header */}
                      <div style={{ padding: '14px 18px', backgroundColor: '#f9fafb', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <div>
                          <p style={{ margin: 0, fontSize: '14px', fontWeight: '800', color: '#111827' }}>{unit.unitName}</p>
                          <p style={{ margin: '2px 0 0', fontSize: '12px', color: '#6b7280' }}>{unit.courseName}</p>
                        </div>
                        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                          <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '9999px', backgroundColor: unit.enrollmentStatus === 'completed' ? '#dbeafe' : '#f3f4f6', color: unit.enrollmentStatus === 'completed' ? '#1d4ed8' : '#6b7280', textTransform: 'capitalize' }}>
                            {unit.enrollmentStatus}
                          </span>
                          <span style={{ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '9999px', backgroundColor: unit.paymentStatus === 'confirmed' ? '#dcfce7' : unit.paymentStatus === 'partial' ? '#dbeafe' : '#fee2e2', color: unit.paymentStatus === 'confirmed' ? '#166534' : unit.paymentStatus === 'partial' ? '#1d4ed8' : '#991b1b', textTransform: 'capitalize' }}>
                            {unit.paymentStatus}
                          </span>
                        </div>
                      </div>

                      {/* Unit financials */}
                      <div style={{ padding: '14px 18px' }}>
                        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px', marginBottom: '12px' }}>
                          {[
                            { l: 'Total Fee', v: unit.totalAmount, c: '#374151', bg: '#f9fafb' },
                            { l: 'Paid', v: unit.paidAmount, c: '#166534', bg: '#f0fdf4' },
                            { l: 'Balance', v: unit.balance, c: unit.balance <= 0 ? '#166534' : '#991b1b', bg: unit.balance <= 0 ? '#f0fdf4' : '#fff7ed' },
                          ].map(x => (
                            <div key={x.l} style={{ textAlign: 'center', padding: '10px', backgroundColor: x.bg, borderRadius: '8px' }}>
                              <p style={{ margin: '0 0 2px', fontSize: '10px', color: '#6b7280', fontWeight: '700', textTransform: 'uppercase' }}>{x.l}</p>
                              <p style={{ margin: 0, fontSize: '15px', fontWeight: '900', color: x.c }}>
                                {x.l === 'Balance' && x.v <= 0 ? '✓ CLEARED' : `KES ${x.v.toLocaleString()}`}
                              </p>
                            </div>
                          ))}
                        </div>
                        <div style={{ marginBottom: '12px' }}>
                          <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '3px' }}>
                            <span style={{ fontSize: '11px', color: '#6b7280' }}>Payment progress</span>
                            <span style={{ fontSize: '11px', fontWeight: '700', color: '#374151' }}>{unit.progressPct}%</span>
                          </div>
                          <div style={{ height: '5px', backgroundColor: '#e5e7eb', borderRadius: '9999px', overflow: 'hidden' }}>
                            <div style={{ height: '100%', width: `${unit.progressPct}%`, backgroundColor: unit.balance <= 0 ? '#10b981' : unit.progressPct >= 50 ? '#f59e0b' : '#ef4444', borderRadius: '9999px' }} />
                          </div>
                        </div>

                        {/* Transactions */}
                        {unit.payments.length > 0 ? (
                          <div style={{ overflowX: 'auto' }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
                              <thead>
                                <tr style={{ backgroundColor: '#f3f4f6' }}>
                                  {['#', 'Date', 'Method', 'Reference', 'Amount', 'Status'].map(h => (
                                    <th key={h} style={{ padding: '8px 10px', textAlign: h === 'Amount' ? 'right' : 'left', fontSize: '10px', fontWeight: '700', textTransform: 'uppercase', color: '#6b7280', borderBottom: '1px solid #e5e7eb' }}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {unit.payments.map((p, pi) => (
                                  <tr key={p.id} style={{ borderBottom: '1px solid #f3f4f6', backgroundColor: pi % 2 === 0 ? 'white' : '#fafafa' }}>
                                    <td style={{ padding: '8px 10px', color: '#6b7280' }}>{pi + 1}</td>
                                    <td style={{ padding: '8px 10px' }}>{new Date(p.createdAt).toLocaleDateString('en-KE', { day: 'numeric', month: 'short', year: 'numeric' })}</td>
                                    <td style={{ padding: '8px 10px', textTransform: 'capitalize' }}>{p.paymentMethod}</td>
                                    <td style={{ padding: '8px 10px', fontFamily: 'monospace', color: '#6b7280', fontSize: '11px' }}>{p.referenceNumber || p.mpesaCode || '—'}</td>
                                    <td style={{ padding: '8px 10px', textAlign: 'right', fontWeight: '700', color: '#111827' }}>KES {parseFloat(p.amount).toLocaleString()}</td>
                                    <td style={{ padding: '8px 10px' }}>
                                      <span style={{ padding: '2px 8px', borderRadius: '9999px', fontSize: '10px', fontWeight: '700', backgroundColor: p.status === 'confirmed' ? '#dcfce7' : p.status === 'rejected' ? '#fee2e2' : '#fef9c3', color: p.status === 'confirmed' ? '#166534' : p.status === 'rejected' ? '#991b1b' : '#854d0e' }}>
                                        {p.status}
                                      </span>
                                    </td>
                                  </tr>
                                ))}
                              </tbody>
                            </table>
                          </div>
                        ) : (
                          <div style={{ textAlign: 'center', padding: '16px', backgroundColor: '#f9fafb', borderRadius: '8px', border: '1px dashed #e5e7eb', color: '#9ca3af', fontSize: '12px' }}>
                            No payment transactions recorded for this unit.
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                  {financialData[financialStudent.id].unitSummaries.length === 0 && (
                    <div style={{ textAlign: 'center', padding: '60px', color: '#6b7280' }}>
                      <p style={{ fontSize: '32px', marginBottom: '12px' }}>📭</p>
                      <p>No unit enrollments found for this student.</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }

        @keyframes slideIn {
          0% { transform: translateX(100%); opacity: 0; }
          100% { transform: translateX(0); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default StudentManagement;
